import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function Register() {
  const { login } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!ageConfirmed) {
      toast.error("Debes confirmar que eres mayor de edad legal y aceptar los términos.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, age_confirmed: ageConfirmed });
      login(data.user);
      toast.success("Cuenta creada. ¡A catar!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1555813456-a7f1fed567c5')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-negro/90 via-negro/40 to-transparent" />
        <div className="relative mt-auto p-14 text-blanco">
          <h1 className="font-serif text-5xl leading-tight mb-4">Tu bodega de<br />notas de cata.</h1>
          <p className="text-blanco/80 max-w-md">Crea fichas completas: examen visual, olfativo y gustativo con puntuación profesional.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-blanco-off">
        <div className="w-full max-w-md animate-fade-up">
          <img src="/vinote-logo.png" alt="Vinonotas" className="w-32 h-32 object-contain rounded-2xl mb-6" />
          <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-2">Crea tu cuenta</p>
          <h2 className="font-serif text-3xl text-negro mb-8">Regístrate</h2>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-grises-600">Nombre</Label>
              <Input data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Tu nombre" required className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-grises-600">Correo electrónico</Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} placeholder="tu@correo.com" required className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-grises-600">Contraseña</Label>
              <PasswordInput testId="register-password-input" value={form.password} onChange={set("password")} placeholder="Mínimo 6 caracteres" required className="mt-1.5 h-11" />
            </div>
            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="age-confirm"
                data-testid="register-age-checkbox"
                checked={ageConfirmed}
                onCheckedChange={(v) => setAgeConfirmed(v === true)}
                className="mt-0.5 data-[state=checked]:bg-borgona-700 data-[state=checked]:border-borgona-700"
              />
              <Label htmlFor="age-confirm" className="text-sm text-grises-600 leading-snug font-normal cursor-pointer">
                Confirmo que soy mayor de edad legal para consumir alcohol y acepto los{" "}
                <a href="/terminos" target="_blank" rel="noreferrer" className="text-borgona-700 hover:underline">Términos</a> y la{" "}
                <a href="/privacidad" target="_blank" rel="noreferrer" className="text-borgona-700 hover:underline">Política de Privacidad</a>.
              </Label>
            </div>
            <Button data-testid="register-submit-btn" type="submit" disabled={loading || !ageConfirmed}
              className="w-full h-11 bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full font-medium transition-colors duration-300 disabled:opacity-50">
              {loading ? "Creando..." : "Crear cuenta"}
            </Button>
          </form>

          <p className="text-sm text-grises-500 mt-6">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" data-testid="go-login-link" className="text-borgona-700 font-medium hover:underline">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
